import { AxiosResponse } from 'axios';
import $api from '@/http/index';
import { IReportData } from '@models/reports/IReportData';
import { IReportScheme } from '@models/reports/IReportScheme';
import { IReportFormats } from '@models/reports/IReportFormats';
import { IReportRun } from '@models/reports/IReportRun';
import { FilesService } from '@services/FilesService';

export class ReportsService {
    static async getData(folder: string, name: string): Promise<AxiosResponse<IReportData>> {
        return $api.get(`reports/data/get`, {
            params: {
                folder: folder,
                name: name,
            },
        });
    }
    static async getScheme(formName: string): Promise<AxiosResponse<IReportScheme>> {
        return $api.get(`reports/scheme/get`, {
            params: {
                name: formName,
            },
        });
    }
    static async getFormats(folder: string, name: string): Promise<AxiosResponse<IReportFormats>> {
        return $api.get(`reports/formats/get`, {
            params: {
                folder: folder,
                name: name,
            },
        });
    }
    /**
     * Построить отчёт. Сервер считает его один раз и сохраняет отрендеренный документ:
     * и просмотр, и выгрузка потом берут готовое, данные заново не запрашиваются.
     */
    static async createRun(
        folder: string,
        name: string,
        form: string,
        data: IReportData,
        subMenu?: string | null,
    ): Promise<IReportRun> {
        const response = await $api.post<IReportRun>(`/reports/runs`, data, {
            // Имя формы нужно ссылке из уведомления: без него открывается список отчётов.
            // Меню — тоже ей: по нему страница находит отчёт в избранном.
            params: { folder, name, form, ...(subMenu ? { subMenu } : {}) },
            suppressErrorNotification: true,
        });
        return response.data;
    }

    /** Состояние построения. */
    static async getRun(runId: string): Promise<IReportRun> {
        const response = await $api.get<IReportRun>(`/reports/runs/${runId}`, {
            suppressErrorNotification: true,
        });
        return response.data;
    }

    /** Запуски пользователя по этому отчёту, новые сверху. */
    static async listRuns(folder: string, name: string, limit = 10): Promise<IReportRun[]> {
        const response = await $api.get<IReportRun[]>(`/reports/runs`, {
            params: { folder, name, limit },
            suppressErrorNotification: true,
        });
        return response.data;
    }

    /**
     * Отменить построение. В очереди отмена настоящая; начатое построение движок не
     * прерывает — его результат будет отброшен.
     */
    static async cancelRun(runId: string): Promise<IReportRun> {
        const response = await $api.post<IReportRun>(`/reports/runs/${runId}/cancel`, undefined, {
            suppressErrorNotification: true,
        });
        return response.data;
    }

    /**
     * Отметиться «страница открыта и ждёт этот отчёт»: пока отметка свежая, сервер не
     * шлёт уведомление о готовности — результат и так перед глазами.
     */
    static async watchRun(runId: string): Promise<void> {
        // Отметка раз в несколько секунд — фоновая: её отказ не ошибка страницы для поддержки.
        await $api.post(`/reports/runs/${runId}/watch`, undefined, {
            suppressErrorNotification: true,
            skipSupportErrorLog: true,
        });
    }

    /**
     * Построенный отчёт для просмотра на странице — PDF. Его готовит сервер: документ
     * движка отчётов браузер разбирает синхронно, и на большом отчёте страница висела
     * десятки секунд, а PDF разбирает воркер pdf.js. Устаревшее открытие отменяют:
     * большой PDF сервер собирает секунды.
     */
    static async getRunPreview(runId: string, signal?: AbortSignal): Promise<ArrayBuffer> {
        const response = await $api.get<ArrayBuffer>(`/reports/runs/${runId}/preview`, {
            responseType: 'arraybuffer',
            suppressErrorNotification: true,
            signal,
        });
        return response.data;
    }

    /**
     * Итог запуска показан пользователю: уведомление о нём в колокольчик больше не нужно.
     * Пока отметки нет, сервер уведомит, как только страница перестанет ждать.
     */
    static async markRunSeen(runId: string): Promise<void> {
        await $api.post(`/reports/runs/${runId}/seen`, undefined, { suppressErrorNotification: true });
    }

    /** Выгрузка построенного отчёта в выбранном формате. */
    static async downloadRunFile(runId: string, format: string): Promise<void> {
        await FilesService.downloadFile(
            `reports/runs/${runId}/file?format=${encodeURIComponent(format)}`,
            { message: 'Отчёт не содержит данных за выбранные параметры.', variant: 'yellow' },
            { notifyOnError: false },
        );
    }

}
